import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DroneService } from '../../../core/data-services/drone.service';
import { OrganizationDrone } from '../../../core/response-models/organization-drone.model';
import { getDroneStatusBadgeClass, getDroneStatusLabel } from '../../../shared/utils/drone-status.utils';
import { AssignDroneModalComponent } from '../../../shared/components/assign-drone-modal.component';
import { ConfirmationModalWithReasonComponent } from '../../../shared/components/confirmation-modal-with-reason.component';

/**
 * Organization Drones Tab Component
 *
 * Displays both ADN-assigned and organization-owned drones in the admin portal.
 * Part of the organization detail page.
 *
 * Features:
 * - View ADN-assigned drones (leased from ADN)
 * - View organization-owned drones (owned by the org)
 * - Assign new ADN drones
 * - Unassign ADN drones
 * - View drone details
 */
@Component({
  selector: 'app-organization-drones-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, AssignDroneModalComponent, ConfirmationModalWithReasonComponent],
  templateUrl: './organization-drones-tab.component.html',
  styleUrls: ['./organization-drones-tab.component.scss'],
})
export class OrganizationDronesTabComponent implements OnInit {
  @Input() organizationId!: string;
  @Input() organizationName!: string;
  @Output() dronesChanged = new EventEmitter<void>();

  // Separate arrays for ADN-assigned and org-owned drones
  adnAssignedDrones: OrganizationDrone[] = [];
  organizationOwnedDrones: OrganizationDrone[] = [];

  loading = true;
  errorMessage = '';
  successMessage = '';
  showUnassigned = false;
  showAssignModal = false;

  showUnassignModal = false;
  processingUnassign = false;
  selectedDrone: OrganizationDrone | null = null;

  // Computed property for total drone count
  get totalDroneCount(): number {
    return this.adnAssignedDrones.length + this.organizationOwnedDrones.length;
  }

  /**
   * Get status badge class for ADN drones
   */
  getDroneStatusBadgeClass(status: string): string {
    return getDroneStatusBadgeClass(status as any);
  }

  /**
   * Get status label for ADN drones
   */
  getDroneStatusLabel(status: string): string {
    return getDroneStatusLabel(status as any);
  }

  /**
   * Get status badge class for organization-owned drones
   */
  getOrgDroneStatusBadgeClass(status: string): string {
    switch (status) {
      case 'active':
        return 'bg-success';
      case 'maintenance':
        return 'bg-warning';
      case 'inactive':
        return 'bg-secondary';
      case 'retired':
        return 'bg-dark';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Get status label for organization-owned drones
   */
  getOrgDroneStatusLabel(status: string): string {
    switch (status) {
      case 'active':
        return 'Active';
      case 'maintenance':
        return 'Maintenance';
      case 'inactive':
        return 'Inactive';
      case 'retired':
        return 'Retired';
      default:
        return status;
    }
  }

  constructor(private droneService: DroneService) {}

  ngOnInit(): void {
    if (!this.organizationId) {
      console.error('OrganizationDronesTabComponent: organizationId is required');
      return;
    }
    this.loadDrones();
  }

  /**
   * Load all drones for this organization (ADN-assigned and org-owned)
   */
  async loadDrones(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      const response = await this.droneService
        .getOrganizationDrones(this.organizationId)
        .toPromise();

      if (response) {
        this.adnAssignedDrones = response.adnAssigned || [];
        this.organizationOwnedDrones = response.organizationOwned || [];
      } else {
        this.adnAssignedDrones = [];
        this.organizationOwnedDrones = [];
      }
    } catch (error: any) {
      console.error('Failed to load organization drones:', error);
      this.errorMessage = 'Failed to load drones';
    } finally {
      this.loading = false;
    }
  }

  /**
   * Reload drones after an action
   */
  async reloadDrones(): Promise<void> {
    await this.loadDrones();
  }

  /**
   * Open assign drone modal
   */
  openAssignDroneModal(): void {
    this.showAssignModal = true;
  }

  /**
   * Close assign drone modal
   */
  closeAssignModal(): void {
    this.showAssignModal = false;
  }

  /**
   * Handle drone assigned event
   */
  async onDroneAssigned(): Promise<void> {
    this.showAssignModal = false;
    this.successMessage = 'Drone assigned successfully';
    await this.reloadDrones();
    this.dronesChanged.emit();

    // Clear success message after 3 seconds
    setTimeout(() => {
      this.successMessage = '';
    }, 3000);
  }

  /**
   * View drone details
   */
  viewDroneDetails(droneId: string): void {
    // Navigate to drone detail page
    window.open(`/drone-detail/${droneId}`, '_blank');
  }

  /**
   * Unassign drone from organization
   */
  async unassignDrone(reason: string): Promise<void> {

    if (!this.selectedDrone){
      return
    }

    try {
      this.processingUnassign = true;
      await this.droneService.returnDrone(this.selectedDrone.id, reason).toPromise();
      this.successMessage = `Drone ${this.selectedDrone.serialNumber} unassigned successfully`;
      await this.reloadDrones();
      this.dronesChanged.emit();

      // Clear success message after 3 seconds
      setTimeout(() => {
        this.successMessage = '';
      }, 3000);
    } catch (error: any) {
      console.error('Failed to unassign drone:', error);
      this.errorMessage = error.message || 'Failed to unassign drone';
    } finally {
      this.processingUnassign = false;
      this.showUnassignModal = false;
    }
  }

  showUnassignedModal(drone: OrganizationDrone): void {
    this.showUnassignModal = true;
    this.selectedDrone = drone;
  }

  closeUnassignModal(): void {
    this.selectedDrone = null;
    this.showUnassignModal = false
  }

  /**
   * View maintenance history for drone
   */
  viewMaintenanceHistory(droneId: string): void {
    // TODO: Implement maintenance history view
    console.log('View maintenance history for drone:', droneId);
    this.errorMessage = 'Maintenance history feature coming soon';
  }

  /**
   * Mark drone as inactive
   */
  async markInactive(drone: OrganizationDrone): Promise<void> {
    if (
      !confirm(`Are you sure you want to mark drone ${drone.serialNumber} as inactive?`)
    ) {
      return;
    }

    // TODO: Implement mark inactive API call
    console.log('Mark drone as inactive:', drone.id);
    this.errorMessage = 'Mark inactive feature coming soon';
  }

  /**
   * Format date for display
   */
  formatDate(date: Date | null): string {
    if (!date) return 'N/A';

    const dateObj = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) {
      return 'Less than 1 hour ago';
    } else if (diffHours < 24) {
      return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    } else {
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
  }

  /**
   * Format assigned date
   */
  formatAssignedDate(date: Date): string {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
}
